import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  fetchProperties,
  fetchProperty,
  fetchUnits,
  createProperty,
  createUnit,
  updateProperty,
  deleteProperty,
  updateUnit,
  deleteUnit,
  type PropertyQuery,
  type PropertyListResponse,
  type CreatePropertyPayload,
  type CreateUnitPayload,
  type UpdatePropertyPayload,
  type UpdateUnitPayload,
} from '@/services/api/properties.api';

export function useProperties(query?: PropertyQuery) {
  return useQuery({
    queryKey: ['properties', query],
    queryFn: () => fetchProperties(query),
    staleTime: 30_000,
  });
}

/** A single row returned by the paginated /properties search. */
export interface PropertySearchRow {
  id: string;
  name: string;
  code?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
}

/**
 * Searchable/paginated properties for the group property switcher (Hotel Groups
 * F1). Thin wrapper over GET /properties?q&page&limit keyed distinctly per
 * (q,page,limit) so each slice caches independently; `keepPreviousData` keeps
 * the list from flashing while the user types or pages.
 */
export function usePropertiesSearch(
  q: string,
  { page = 1, limit = 20 }: { page?: number; limit?: number } = {},
) {
  const query: PropertyQuery = { page, limit, ...(q ? { q } : {}) };
  return useQuery({
    queryKey: ['properties', { q: q || undefined, page, limit }],
    queryFn: () => fetchProperties(query) as Promise<PropertyListResponse<PropertySearchRow>>,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: ['properties', id],
    queryFn: () => fetchProperty(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useUnits(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['units', propertyId],
    queryFn: () => fetchUnits(propertyId!),
    enabled: !!propertyId,
    staleTime: 30_000,
  });
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePropertyPayload) => createProperty(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useCreateUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, payload }: { propertyId: string; payload: CreateUnitPayload }) =>
      createUnit(propertyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}

export function useUpdateProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePropertyPayload }) =>
      updateProperty(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useDeleteProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProperty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useUpdateUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, unitId, payload }: { propertyId: string; unitId: string; payload: UpdateUnitPayload }) =>
      updateUnit(propertyId, unitId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, unitId }: { propertyId: string; unitId: string }) =>
      deleteUnit(propertyId, unitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });
}
